"use client"

import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination"
import { useHandleSearchParams } from "@/lib/hooks/useHandleSearchParams"
import { cn } from "@/lib/utils"
import { Blog } from "@types"

export default function PaginationComponent({
  blog,
  postCount,
}: {
  blog?: Blog
  postCount: number
}) {
  // Determine page size based upon blog postListType setting (default to 10)
  let pageSize = 10
  switch (blog?.theme?.postListType) {
    case "grid":
      pageSize = 12
      break
    case "feed":
      pageSize = 10
      break
    case "full-post":
      pageSize = 5
  }

  const totalPages = Math.ceil(postCount / pageSize)

  const { handle: handlePage, currentValue } = useHandleSearchParams("page")

  const currentPage = parseInt(currentValue || "1")

  const renderPaginationItems = () => {
    const items = []

    // Always show the first page
    items.push(
      <PaginationItem
        key="1"
        onClick={() => handlePage("1")}
        className="cursor-pointer"
      >
        <PaginationLink isActive={currentPage === 1}>1</PaginationLink>
      </PaginationItem>
    )

    if (currentPage > 3) {
      // Show ellipsis if the current page is more than 3 away from the first page
      items.push(
        <PaginationItem key="start-ellipsis">
          <PaginationEllipsis />
        </PaginationItem>
      )
    }

    // Show pages around the current page
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      items.push(
        <PaginationItem
          key={i}
          onClick={() => handlePage(i.toString())}
          className="cursor-pointer"
        >
          <PaginationLink isActive={currentPage === i}>{i}</PaginationLink>
        </PaginationItem>
      )
    }

    if (currentPage < totalPages - 2) {
      // Show ellipsis if the current page is more than 2 away from the last page
      items.push(
        <PaginationItem key="end-ellipsis">
          <PaginationEllipsis />
        </PaginationItem>
      )
    }

    // Always show the last page
    if (totalPages > 1) {
      items.push(
        <PaginationItem
          key={totalPages}
          onClick={() => handlePage(totalPages.toString())}
          className="cursor-pointer"
        >
          <PaginationLink isActive={currentPage === totalPages}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      )
    }

    return items
  }

  // Don't show pagination component if there is only one page
  if (totalPages <= 1) return null

  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={() => {
              if (currentPage === 1) return
              handlePage((currentPage - 1).toString())
            }}
            className={cn(currentPage === 1 ? "opacity-50" : "cursor-pointer")}
          />
        </PaginationItem>
        {renderPaginationItems()}
        <PaginationItem>
          <PaginationNext
            onClick={() => {
              if (currentPage === totalPages) return
              handlePage((currentPage + 1).toString())
            }}
            className={cn(
              currentPage === totalPages ? "opacity-50" : "cursor-pointer"
            )}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}
